import {
  getCurrentAttendance,
  Giustificativo,
  saveAttendance,
} from "@/api/openapi";
import { AttendancesContext, PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  attendanceToTempAttendance,
  removeGiustificativoFromEmployee,
  tempAttendanceToAttendance,
  updateAttandenceAfterRegularHoursChange,
} from "../../../attendanceUtils";

export function useAttendanceMobileEditForm() {
  const { lang, selectedMandateId } = useContext(PspContext);
  const {
    attendanceMonth,
    setAttendanceMonth,
    setTempAttendanceMonth,
    tempAttendanceMonth,
    selectedMonth,
    selectedYear,
    isSubmitted,
    setIsSubmitted,
    setSubmittedAt,
    setSelectedMonth,
    setSelectedYear,
  } = useContext(AttendancesContext);

  const {
    employeeMatricola: employeeMatricolaString,
    employeeFiliale: employeeFilialeString,
    year: yearString,
    month: monthString,
    day: dayString,
  } = useParams();

  const employeeMatricola = employeeMatricolaString
    ? Number.parseInt(employeeMatricolaString)
    : NaN;
  const employeeFiliale = employeeFilialeString
    ? Number.parseInt(employeeFilialeString)
    : NaN;
  const year = yearString ? Number.parseInt(yearString) : NaN;
  const month = monthString ? Number.parseInt(monthString) : NaN;
  const day = dayString ? Number.parseInt(dayString) : NaN;

  const date = new Date(year, month - 1, day);

  const [currentScratchValue, setCurrentScratchValue] = useState<string | null>(
    null,
  );
  const [currentEditingValue, setCurrentEditingValue] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [
    addGiustificativoBottomSheetOpen,
    setAddGiustificativoBottomSheetOpen,
  ] = useState(false);

  const [
    removeGiustificativoConfirmationOpen,
    setRemoveGiustificativoConfirmationOpen,
  ] = useState(false);
  const [wantsToRemoveGiustificativo, setWantsToRemoveGiustificativo] =
    useState<Giustificativo | null>(null);

  useEffect(() => {
    if (!attendanceMonth && selectedMandateId) {
      getCurrentAttendance({
        query: { mandate_id: selectedMandateId, month, year },
      }).then(({ data, error }) => {
        if (error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setAttendanceMonth(data.data);
          setTempAttendanceMonth(attendanceToTempAttendance(data.data));
          setIsSubmitted(data.isSubmitted);
          setSubmittedAt(data.submittedAt ? new Date(data.submittedAt) : null);
        }
      });
    }
    if (!tempAttendanceMonth && attendanceMonth) {
      setTempAttendanceMonth(attendanceToTempAttendance(attendanceMonth));
    }
  }, [
    attendanceMonth,
    lang,
    month,
    setSubmittedAt,
    selectedMandateId,
    setAttendanceMonth,
    setIsSubmitted,
    setTempAttendanceMonth,
    tempAttendanceMonth,
    year,
  ]);

  const tempAttendance = tempAttendanceMonth?.attendances.find(
    (a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale,
  );

  const employee = tempAttendance?.employee;
  const giustificativi = tempAttendance?.days[day - 1].giustificativi;

  const tempAttendanceDay = tempAttendance?.days[day - 1];
  useEffect(() => {
    if (selectedMonth && selectedYear) return;

    setSelectedMonth(month);
    setSelectedYear(year);
  }, [
    month,
    selectedMonth,
    selectedYear,
    setSelectedMonth,
    setSelectedYear,
    year,
  ]);

  function onBlurGiustificativoInput() {
    if (!tempAttendanceMonth) {
      toast(translate("somethingWentWrong", lang));
      throw new Error(
        "onBlur was triggered on a giustificativo input, but tempAttendanceMonth was undefined.",
      );
    }
    if (!selectedMandateId) {
      toast(translate("noMandateSelected", lang));
      return;
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }
    setIsSaving(true);
    const newAttendance = tempAttendanceToAttendance(tempAttendanceMonth);
    setAttendanceMonth(newAttendance);
    saveAttendance({
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
      query: { draft: true },
    }).then(({ error, data }) => {
      setIsSaving(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
    });
  }

  function resetState() {
    setCurrentEditingValue(null);
    setCurrentEditingValue(null);
    setCurrentScratchValue(null);
  }

  const employeeAttendance = attendanceMonth?.attendances.find(
    (a) =>
      a.employee.matricola === employeeMatricola &&
      a.employee.filiale === employeeFiliale,
  );
  function onBlurRegularHoursInput() {
    if (attendanceMonth === undefined) {
      toast(translate("somethingWentWrong", lang));
      resetState();
      throw new Error(
        "onBlur was triggered for a regular hours input, but attendanceMonth was undefined",
      );
    }
    if (selectedMandateId === null) {
      toast(translate("noMandateSelected", lang));
      resetState();
      return;
    }
    if (currentScratchValue === null) {
      toast(translate("somethingWentWrong", lang));
      resetState();
      throw new Error(
        "onBlur was triggered on regular hours input, but currentScratchValue is null",
      );
    }
    if (selectedMonth === undefined || selectedYear === undefined) {
      return;
    }

    if (!employeeAttendance) {
      throw new Error(
        "No employee with matching filiale and matricola was found.",
      );
    }
    setIsSaving(true);
    const newAttendance = updateAttandenceAfterRegularHoursChange(
      attendanceMonth,
      employeeAttendance,
      currentScratchValue,
      currentEditingValue,
      date.getDate() - 1,
      lang,
      resetState,
    );

    setAttendanceMonth(newAttendance);
    setTempAttendanceMonth(attendanceToTempAttendance(newAttendance));

    saveAttendance({
      query: { draft: true },
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      setIsSaving(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        throw error;
      }
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
    });
  }

  function openRemoveGiustificativoConfirmation(
    giustificativo: Giustificativo,
  ) {
    setWantsToRemoveGiustificativo(giustificativo);
    setRemoveGiustificativoConfirmationOpen(true);
  }

  function closeRemoveGiustificativoConfirmation() {
    setRemoveGiustificativoConfirmationOpen(false);
    setTimeout(() => {
      setWantsToRemoveGiustificativo(null);
    }, 250);
  }

  function onRemoveGiustificativo() {
    if (
      !wantsToRemoveGiustificativo ||
      !removeGiustificativoConfirmationOpen ||
      !attendanceMonth ||
      !employeeAttendance ||
      selectedMonth === undefined ||
      selectedYear === undefined ||
      selectedMandateId === null
    )
      return;

    const newAttendance = removeGiustificativoFromEmployee(
      attendanceMonth,
      wantsToRemoveGiustificativo.type,
      employeeAttendance.employee.matricola,
      employeeAttendance.employee.filiale,
    );

    setIsSaving(true);
    saveAttendance({
      query: { draft: true },
      body: {
        data: newAttendance.attendances,
        mandateId: selectedMandateId,
        month: selectedMonth,
        year: selectedYear,
      },
    }).then(({ data, error }) => {
      setIsSaving(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }
      closeRemoveGiustificativoConfirmation();
      setAttendanceMonth(data);
      setTempAttendanceMonth(attendanceToTempAttendance(data));
    });
  }

  return {
    lang,
    date,
    employee,
    giustificativi,
    tempAttendanceDay,
    currentScratchValue,
    setCurrentScratchValue,
    setCurrentEditingValue,
    onBlurGiustificativoInput,
    onBlurRegularHoursInput,
    setTempAttendanceMonth,
    employeeMatricola,
    employeeFiliale,
    isSaving,
    setIsSaving,
    isSubmitted,
    addGiustificativoBottomSheetOpen,
    setAddGiustificativoBottomSheetOpen,
    employeeAttendance,
    openRemoveGiustificativoConfirmation,
    closeRemoveGiustificativoConfirmation,
    removeGiustificativoConfirmationOpen,
    wantsToRemoveGiustificativo,
    onRemoveGiustificativo,
  };
}
